"use server";
import { pointFrom } from "@excalidraw/math";
import {
  newElement,
  newLinearElement,
  newTextElement,
} from "@excalidraw/element/newElement";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

export interface Shape {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  text?: string;
}

export const shapeToElement = (shape: Shape): NonDeletedExcalidrawElement => {
  switch (shape.type) {
    case "line":
      return newLinearElement({
        type: "line",
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
        points: [pointFrom(0, 0), pointFrom(shape.width, shape.height)],
      });
    case "text":
      return newTextElement({
        text: shape.text || "",
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
      });
    default:
      return newElement({
        type: shape.type as any,
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
      });
  }
};

/**
 * 画像 URL を受け取り、/api/analyse-image から返ってきた
 * ElementFactoryArgs[] を Shape[] にマッピングして返す。
 */
export async function processImage(
  imageUrl: string,
): Promise<NonDeletedExcalidrawElement[]> {
  const urlToBase64 = async (url: string): Promise<string> => {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(",")[1]); // "data:image/...;base64," を除去
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const base64 = imageUrl.startsWith("data:image/") //
    ? imageUrl.split(",")[1]
    : await urlToBase64(imageUrl);

  const res = await fetch("/api/analyse-image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ imageUrl: `data:image/jpeg;base64,${base64}` }),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }

  const args: Shape[] = await res.json();

  return args.map(shapeToElement);
}
